import { useMemo, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/shared/lib/utils";
import type {
  AnchorPosition,
  FilterConfig,
  LogoAnchor,
  OverlayConfig,
  QrcodeAnchor,
  TemplateAnchors,
} from "../../api/posterService";

import { resolveAnchorXY } from "./canvas/anchorMath";
import { filterToCss } from "./canvas/filterCss";
import { useFitBox } from "@/shared/hooks/useFitBox";

/**
 * v2.9 通用海报实时预览。
 *
 * 同一个组件在两个上下文用:
 * - Step5 模版选择: base = 占位行业图,overlay = 模版默认 anchors,brand = 用户 Step2 上传
 * - Detail 编辑器: base = AI 出的裸底图,overlay = 当前 asset.overlay_config,brand = task 提交时的素材
 *
 * 渲染规则:
 * - 用 absolute-position div 把 logo/qrcode/contact 叠到底图上
 * - filter 走 CSS `filter:` 实时反映亮度/对比/饱和/冷暖
 * - 字体大小 = anchor.font_size + filter.font_size_offset
 *
 * 跟 ai-service compositor 输出预期 ≈ 5% 像素差(字体抗锯齿、warmth 近似不同),
 * spec §11 容忍。"保存调整"后端走 PIL 重 compose 出真成品。
 */
export interface PosterCanvasProps {
  /** 底图 URL,Step5 是占位行业图,Detail 是 base_image_url。空时显示灰底。 */
  baseUrl?: string | null;
  /** 模版默认 anchors。从这套基础上叠加 overlay overrides。 */
  templateAnchors: TemplateAnchors;
  /** 用户调整层。任一字段缺省则回退 templateAnchors 的对应值。 */
  overlay?: OverlayConfig | null;
  /** 滤镜调整。 */
  filter?: FilterConfig | null;
  /** 用户素材。 */
  brand: BrandPreview;
  /** 容器宽度(px)。高度按 1:1 计算,海报输出方形/竖版/横版差异由 sizePreset 控,这里默认 1:1 预览。 */
  width?: number;
  /** 1 = 实物方形;支持自定义比例(如竖版 1080x1440 → 0.75)。 */
  aspectRatio?: number;
  className?: string;
  /**
   * 查看缩放工具条(2026-07-30,7.29 反馈任务 17)。
   *
   * ★缺陷现场:用户按 Ctrl+- 想缩小,图**反而变大**。真因不是我们抢了快捷键 ——
   *   全仓根本没有 ctrl 缩放监听(截图里那个「67% − + 重置」气泡是 **Chrome 原生的**)。
   *   而是画布 `width:100%` 由容器宽度驱动:浏览器缩小页面 → CSS 视口变宽 → 布局跨断点
   *   → 主画布拿到更大的宽度份额 → 屏幕上真的变大。所以要给的是**我们自己的查看缩放**。
   * ★**不接管 Ctrl+加减 / Ctrl+滚轮** —— 那是浏览器原生行为,无障碍工具依赖它。
   *   我们的滚轮缩放挂在 **Alt** 上,不和浏览器抢。
   */
  zoomable?: boolean;
  /**
   * 盒子驱动取景(「适屏」)。开了之后画布按 `min(可用宽, 可用高 × 比例)` 整张进框,
   * 永不越屏;可用区由**本组件自己量**(见 `useFitBox`),调用方只要给一个有确定
   * 宽高的父容器(`flex-1 + min-h-0`)。
   *
   * ★不开 = 存量的**宽度驱动**行为(`width:100%`,高度随比例长出去)。默认值不改,
   *   因为 advisor H5 那套画布也在用这个组件 —— 这是**加档不是改档**。
   * ★实测背景(2026-08-07):9:16 主图在 1920 上被撑成 1354×2407,整页 2.80 屏;
   *   屏幕越大滚得越多,因为视口高度从来没参与过取景。
   * ★要 zoomable 才有意义(适屏是缩放档之一);不 zoomable 时它只影响画布宽度。
   */
  fit?: boolean;
}

export interface BrandPreview {
  logoUrl?: string | null;
  qrcodeUrl?: string | null;
  /** 第一行电话 / 第二行邮箱,空字段过滤掉。 */
  contactLines: string[];
  /** 海报主标题 — 渲染在画面顶部居中,与 QR 解耦。 */
  posterTitle?: string;
  /** 海报副标题 — 主标题下一行,更小更灰。 */
  posterSubtitle?: string;
}

/** 查看缩放档:fit = 适应宽度(存量行为);数字 = 相对**原始像素**的倍率。 */
type ZoomLevel = "fit" | number;
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

export function PosterCanvas({
  baseUrl,
  templateAnchors,
  overlay,
  filter,
  brand,
  width = 320,
  aspectRatio = 1,
  className,
  zoomable = false,
  fit = false,
}: PosterCanvasProps) {
  const { t } = useTranslation("poster");
  const containerRef = useRef<HTMLDivElement>(null);
  // 适屏档的可用区 = 下面那个 viewport(**不含**工具条),所以量在 viewport 上;
  // 量整块会把工具条高度算进去,图就比框高出一条,又要滚。
  const fitBox = useFitBox(aspectRatio);
  const fitWidth = fit ? fitBox.width : null;
  const [renderSize, setRenderSize] = useState({ w: width, h: width / aspectRatio });
  const [zoom, setZoom] = useState<ZoomLevel>("fit");
  /** 底图原始像素宽 —— 「100% 原始像素」这一档要它才算得出。 */
  const [naturalW, setNaturalW] = useState<number | null>(null);
  const clampZoom = (z: number) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));
  /** 当前实际倍率(fit 档按渲染宽/原始宽反算,用于百分比读数)。 */
  const effectiveZoom = zoom === "fit" ? (naturalW ? renderSize.w / naturalW : null) : zoom;
  const zoomedWidth = zoom === "fit" || !naturalW ? undefined : Math.round(naturalW * zoom);
  const stepZoom = (delta: number) =>
    setZoom((z) => clampZoom((typeof z === "number" ? z : (effectiveZoom ?? 1)) + delta));

  // 跟随容器实际宽度自适应(保留 aspectRatio 比例)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? width;
      setRenderSize({ w, h: w / aspectRatio });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [aspectRatio, width]);

  const merged = useMemo(() => mergeAnchors(templateAnchors, overlay), [templateAnchors, overlay]);

  const cssFilter = useMemo(() => filterToCss(filter), [filter]);

  const canvas = (
    <div
      ref={containerRef}
      data-poster-canvas={fit ? "fit" : "width"}
      className={cn(
        "relative overflow-hidden rounded-lg bg-muted",
        "ring-1 ring-border",
        className
      )}
      /* zoom 档给显式像素宽:ResizeObserver 会把它写进 renderSize,
         logo/QR/标题各层本来就按 renderSize 定位 —— 缩放自动跟随,不用逐层改。
         ★适屏档同理走显式 px(fitWidth):同一条路,同一套坐标换算,overlay 不用改。 */
      style={{ width: zoomedWidth ?? fitWidth ?? "100%", aspectRatio, flexShrink: 0 }}
    >
      {/* 底图 */}
      {baseUrl ? (
        <img
          src={baseUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{ filter: cssFilter }}
          draggable={false}
          onLoad={(e) => setNaturalW(e.currentTarget.naturalWidth || null)}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
          {t("canvas.preview")}
        </div>
      )}

      {/* Logo / QR 叠加 — 用户没绑就不渲染。
          V6 fix4 (2026-05-12 fix3) — 之前的 PlaceholderLayer 兜底是给老 Wizard Step5
          "模板选择"场景设计的(画虚线框告诉用户'位置预览'),但 V20 wizard 已删,
          现在只有 AssetEditor 在用 PosterCanvas。用户"本次不使用" logo / QR 时,
          切到「重画」tab 还看到 2 个虚线框,反复反馈。彻底拿掉 placeholder。 */}
      {brand.logoUrl && (
        <LogoLayer
          url={brand.logoUrl}
          anchor={merged.logo}
          canvasW={renderSize.w}
          canvasH={renderSize.h}
        />
      )}
      {brand.qrcodeUrl && (
        <QrcodeLayer
          url={brand.qrcodeUrl}
          anchor={merged.qrcode}
          canvasW={renderSize.w}
          canvasH={renderSize.h}
        />
      )}

      {/* 海报标题叠加 — 顶部居中,优先级最高的视觉锚点,与 QR 解耦 */}
      {(brand.posterTitle || brand.posterSubtitle) && (
        <HeadlineLayer
          title={brand.posterTitle}
          subtitle={brand.posterSubtitle}
          canvasW={renderSize.w}
          canvasH={renderSize.h}
        />
      )}

      {/* s15.6 (2026-05-08): 联系方式渲染整块砍掉 — v3 之后 AI 直接画电话/邮箱文字,
          compositor 不消费 contact overlay, canvas 上画"联系方式占位框"会误导用户. */}
    </div>
  );

  if (!zoomable) return canvas;

  const pct = effectiveZoom == null ? null : Math.round(effectiveZoom * 100);
  const btn =
    "rounded px-1.5 py-0.5 text-2xs transition-colors hover:bg-muted disabled:opacity-40";
  // 盒子驱动时这一档是「适屏」(整张进框),宽度驱动时仍是「适应宽度」(高度随比例长)。
  // 名字必须跟着行为走 —— 适屏档下写「适应宽度」是页面在撒谎。
  const fitLabel = fit ? "适屏" : "适应宽度";
  return (
    <div className={cn(fit ? "flex h-full min-h-0 flex-col gap-1.5" : "space-y-1.5")}>
      {/* 查看缩放工具条(任务 17)。★不接管 Ctrl+加减/Ctrl+滚轮 —— 那是浏览器原生行为,
          无障碍工具依赖它;我们的滚轮挂 Alt。 */}
      <div
        className="flex shrink-0 items-center gap-1 text-muted-foreground"
        data-canvas-zoom={zoom}
      >
        <button
          type="button"
          className={btn}
          onClick={() => setZoom("fit")}
          aria-pressed={zoom === "fit"}
        >
          {fitLabel}
        </button>
        <button
          type="button"
          className={btn}
          onClick={() => setZoom(1)}
          aria-pressed={zoom === 1}
          disabled={!naturalW}
          title={naturalW ? `原始 ${naturalW}px 宽` : "底图还没加载完"}
        >
          100%
        </button>
        <span className="mx-0.5 h-3 w-px bg-border" />
        <button
          type="button"
          className={btn}
          aria-label="缩小"
          disabled={!naturalW}
          onClick={() => stepZoom(-ZOOM_STEP)}
        >
          −
        </button>
        <span className="min-w-[2.6rem] text-center text-2xs tabular-nums">
          {pct == null ? "—" : `${pct}%`}
        </span>
        <button
          type="button"
          className={btn}
          aria-label="放大"
          disabled={!naturalW}
          onClick={() => stepZoom(ZOOM_STEP)}
        >
          +
        </button>
        <span className="ml-1 text-2xs opacity-70">Alt+滚轮</span>
      </div>
      <div
        ref={fit ? fitBox.ref : undefined}
        /* ★适屏档:高度由外面的 Stage 给(flex-1 + min-h-0),这里只负责居中 + 放大后可滚。
           `max-h-[70vh]` 是宽度驱动时代的补丁 —— 它按视口比例切一刀,和「图有多高」无关,
           实测长图在它下面只露 7~8%。盒子驱动之后不需要它。 */
        className={cn(
          fit
            ? "flex min-h-0 flex-1 items-center justify-center overflow-auto rounded-lg"
            : "max-h-[70vh] overflow-auto rounded-lg"
        )}
        onWheel={(e) => {
          // 只认 Alt:不带修饰键时让页面正常滚动,Ctrl 留给浏览器自己的缩放。
          if (!e.altKey || !naturalW) return;
          e.preventDefault();
          stepZoom(e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP);
        }}
      >
        {canvas}
      </div>
    </div>
  );
}

/**
 * 占位层 — brand 资源缺失时画占位框给用户视觉反馈,虚线 + "未上传"提示 + 暗示补素材。
 */
function PlaceholderLayer({
  label,
  subLabel,
  anchor,
  canvasW,
  canvasH,
  fixedRatio,
  aspectSquare,
}: {
  label: string;
  subLabel: string;
  // size_ratio 在 LogoAnchor / QrcodeAnchor 上有,在 ContactAnchor 没有 — 用 fixedRatio 兜底
  anchor: { position: AnchorPosition; padding: number; size_ratio?: number };
  canvasW: number;
  canvasH: number;
  fixedRatio?: number;
  aspectSquare: boolean;
}) {
  const ratio = anchor.size_ratio ?? fixedRatio ?? 0.18;
  const targetW = canvasW * ratio;
  const targetH = aspectSquare ? targetW : targetW * 0.4;
  const { x, y } = resolveAnchorXY(
    canvasW,
    canvasH,
    targetW,
    targetH,
    anchor.position,
    anchor.padding
  );
  return (
    <div
      className="absolute flex flex-col items-center justify-center rounded-md border border-dashed border-foreground/40 bg-background/40 backdrop-blur-[2px] text-center transition-all duration-200"
      style={{
        left: x,
        top: y,
        width: targetW,
        height: targetH,
      }}
    >
      <span className="text-2xs font-medium text-foreground/70 leading-none">{label}</span>
      <span className="text-2xs text-foreground/40 leading-none mt-0.5">{subLabel}</span>
    </div>
  );
}

function LogoLayer({
  url,
  anchor,
  canvasW,
  canvasH,
}: {
  url: string;
  anchor: LogoAnchor;
  canvasW: number;
  canvasH: number;
}) {
  const { t } = useTranslation("poster");
  const [broken, setBroken] = useState(false);
  // logo 实际尺寸 ratio 不知道(图片未加载完),先按方形估算占位,加载完再纠正不破坏布局
  const targetW = canvasW * anchor.size_ratio;
  const { x, y } = resolveAnchorXY(
    canvasW,
    canvasH,
    targetW,
    targetW,
    anchor.position,
    anchor.padding
  );
  if (broken) {
    return (
      <PlaceholderLayer
        label={t("canvas.logoLabel")}
        subLabel={t("canvas.loadFailed")}
        anchor={anchor}
        canvasW={canvasW}
        canvasH={canvasH}
        aspectSquare
      />
    );
  }
  return (
    <img
      src={url}
      alt="logo"
      className="absolute object-contain"
      style={{
        left: x,
        top: y,
        width: targetW,
        height: targetW,
      }}
      draggable={false}
      onError={() => setBroken(true)}
    />
  );
}

function QrcodeLayer({
  url,
  anchor,
  canvasW,
  canvasH,
}: {
  url: string;
  anchor: QrcodeAnchor;
  canvasW: number;
  canvasH: number;
}) {
  const { t } = useTranslation("poster");
  const [broken, setBroken] = useState(false);
  const targetW = canvasW * anchor.size_ratio;
  const boxPad = 4;
  const boxW = targetW + boxPad * 2;
  const { x, y } = resolveAnchorXY(canvasW, canvasH, boxW, boxW, anchor.position, anchor.padding);

  if (broken) {
    return (
      <PlaceholderLayer
        label={t("canvas.qrLabel")}
        subLabel={t("canvas.loadFailed")}
        anchor={anchor}
        canvasW={canvasW}
        canvasH={canvasH}
        aspectSquare
      />
    );
  }
  return (
    <div
      className="absolute bg-white"
      style={{ left: x, top: y, width: boxW, height: boxW, padding: boxPad }}
    >
      <img
        src={url}
        alt="qrcode"
        className="h-full w-full object-contain"
        draggable={false}
        onError={() => setBroken(true)}
      />
    </div>
  );
}

/**
 * 顶部居中海报标题/副标题层。与 ai-service compositor._draw_headline 同构。
 *
 * 设计取舍 (2026-05-06): 不加底色板,文字直接放在 AI 设计的标题区上,
 * 用 text-shadow 模拟 PIL 的 white stroke, 保证可读但不遮挡 AI 视觉。
 */
function HeadlineLayer({
  title,
  subtitle,
  canvasW,
  canvasH,
}: {
  title?: string;
  subtitle?: string;
  canvasW: number;
  canvasH: number;
}) {
  if (!title && !subtitle) return null;
  // 主标题字号 = 画布宽 6% (clamp 18-48 px on web preview, PIL 输出按真实尺寸 36-96)
  const titleSize = Math.max(18, Math.min(48, Math.round(canvasW * 0.06)));
  const subtitleSize = Math.max(11, Math.round(titleSize * 0.55));
  const topPadding = Math.round(canvasH * 0.04);

  // 模拟 PIL stroke 的 4 方向 white shadow — 保证深色字在任意 AI 底色上可读
  const titleStrokePx = Math.max(1.5, titleSize / 18);
  const subtitleStrokePx = Math.max(1, subtitleSize / 14);
  const strokeShadow = (px: number) =>
    `-${px}px -${px}px 0 #fff, ${px}px -${px}px 0 #fff, -${px}px ${px}px 0 #fff, ${px}px ${px}px 0 #fff, 0 ${px}px ${px * 1.5}px rgba(0,0,0,0.15)`;

  return (
    <div
      className="absolute pointer-events-none flex flex-col items-center animate-in fade-in slide-in-from-top-2 duration-300"
      style={{
        left: 0,
        right: 0,
        top: topPadding,
        paddingLeft: Math.max(16, canvasW * 0.04),
        paddingRight: Math.max(16, canvasW * 0.04),
      }}
    >
      <div className="flex flex-col items-center max-w-full">
        {title && (
          <span
            className="font-bold leading-tight text-center tracking-tight"
            style={{
              fontSize: titleSize,
              color: "#0f172a",
              letterSpacing: "-0.01em",
              textShadow: strokeShadow(titleStrokePx),
            }}
          >
            {title}
          </span>
        )}
        {subtitle && (
          <span
            className="leading-snug text-center"
            style={{
              fontSize: subtitleSize,
              color: "#334155",
              marginTop: title ? Math.max(2, titleSize * 0.12) : 0,
              textShadow: strokeShadow(subtitleStrokePx),
            }}
          >
            {subtitle}
          </span>
        )}
      </div>
    </div>
  );
}

// s15.6 (2026-05-08): ContactLayer 整段删掉 — v3 之后 AI 直接画文字, compositor / canvas
// 都不再消费 contact overlay. 数据流 brand.contactLines 仍保留以兼容 BrandPreview interface,
// 但不渲染.

/** 合并模版默认 anchors + 用户 overlay overrides。overlay 任一字段缺省则用模版默认。 */
function mergeAnchors(
  template: TemplateAnchors,
  overlay: OverlayConfig | null | undefined
): TemplateAnchors {
  if (!overlay) return template;
  return {
    logo: {
      ...template.logo,
      position: overlay.logo?.position ?? template.logo.position,
      size_ratio: overlay.logo?.size_ratio ?? template.logo.size_ratio,
    },
    qrcode: {
      ...template.qrcode,
      position: overlay.qrcode?.position ?? template.qrcode.position,
      size_ratio: overlay.qrcode?.size_ratio ?? template.qrcode.size_ratio,
    },
    contact: {
      ...template.contact,
      position: overlay.contact?.position ?? template.contact.position,
      font_size: overlay.contact?.font_size ?? template.contact.font_size,
    },
  };
}
